import { EXPORT_TYPE } from '@src/constants/presentation';
import NormalProjectIcon from '@src/assets/icons/project-normal.svg';
import PresentationProjectIcon from '@src/assets/icons/project-presentation.svg';
import SrtProjectIcon from '@src/assets/icons/project-srt.svg';
import VideoProjectIcon from '@src/assets/icons/project-video.svg';

export const MAX_TITLE_LENGTH = 100;

export const HEADER_HEIGHT = 80;

export const AUDIO_PLAYER_HEIGHT = 62;

export const TOOLBAR_HEIGHT = 64;

export const CHARACTER_LIMIT_BANNER_HEIGHT = 40;

export const EXPORT_BANNER_HEIGHT = 40;

export const MAX_CHARACTER_PER_BLOCK = 1000;

// Max characters per block when using basic SRT (dubbing) project with BLOCK_WITH_DUBBING_PROJECT feature
export const MAX_CHARACTER_PER_BLOCK_SRT = 1000;

// Minimum gap between blocks to allow inserting a new block (in seconds)
export const MIN_GAP_BETWEEN_BLOCKS = 0.002;

// Margin time when inserting a new block between two blocks (in seconds)
export const BLOCK_MARGIN = 0.001;
export const DURATION_BLOCK_MARGIN = 2; // 2 seconds

export const PROJECT_PRODUCT = {
  DUBBING: 'DUBBING',
  TTS: 'TTS',
  PRESENTATION: 'PRESENTATION',
};

export const VALID_VOLUME = { MIN: 0, MAX: 100 };

// Keys of `project.volumes` — the mixer roles the Backend `volumes` field uses.
export const VOLUME_TRACK = {
  VOICEOVER: 'voiceover',
  MEDIA: 'media',
  MUSIC: 'music',
};

// Every track starts audible (0..1 scale, matching `project.volumes`).
export const DEFAULT_TRACK_VOLUMES = {
  [VOLUME_TRACK.VOICEOVER]: 1,
  [VOLUME_TRACK.MEDIA]: 1,
  [VOLUME_TRACK.MUSIC]: 1,
};

export const CONVERTING_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const PLAYING_TYPE = {
  BLOCK: 'BLOCK',
  ALL_BLOCKS: 'ALL_BLOCKS',
};

export const DIRECTION = {
  UP: 'up',
  DOWN: 'down',
};

export const DISPLAY_SUCCESS_ICON_DURATION = 3000;

export const NUMBER_OF_BLOCKS_PER_BATCH = 1;

export const AUDIO_MODE = {
  JOINED: 'joined',
  SEPARATE: 'separate',
};

export const FETCH_BLOCKS_STATUS_INTERVAL = 10 * 1000;

export const FETCH_TRANSLATE_STATUS_INTERVAL = 5 * 1000;

export const FETCH_EXTRACTION_STATUS_INTERVAL = 5 * 1000;
export const FETCH_EXTRACTION_STATUS_INTERVAL_LARGE = 15 * 1000;
export const PPTX_SMALL_SLIDE_THRESHOLD = 30;

export const EXTRACTION_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

// Status of the source waveform generated on the backend (aivoice-video).
export const WAVEFORM_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const FETCH_EXPORT_STATUS_INTERVAL = 5 * 1000;

// First wait of the project-wide progress poll, which then doubles up to
// FETCH_EXPORT_STATUS_INTERVAL as in `apis/pronunciationCheck.js`. A small
// export finishes in about a second; a late one is answered no slower than a
// flat interval would have.
export const EXPORT_POLL_FIRST_INTERVAL = 1000;

// Consecutive failed progress ticks tolerated before an export is called failed.
// A single failure is not evidence the export died: the record can be missing
// for a moment, and the endpoint answers a rejected envelope with HTTP 200.
export const EXPORT_POLL_FAILURE_BUDGET = 12;

export const EXPORT_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

// Fallback for the days an exported file stays downloadable (BR_2). The real
// number comes from the user's package through `useExportRetentionPeriod`; this
// one only stands in until the user has loaded.
export const EXPORT_RETENTION_PERIOD = 3;

// One page of the export history tab: the newest records on open, and the same
// number again each time the list is scrolled to the end.
export const EXPORT_HISTORY_LIMIT = 10;

// Export types the history tab lists. A whitelist rather than "everything
// except SEPARATE_AUDIO_PARTIAL" so a new internal export type cannot leak into
// the UI by default. SEPARATE_AUDIO_PARTIAL is the block-toolbar export, which
// is out of scope for the history.
export const EXPORT_HISTORY_TYPES = [
  EXPORT_TYPE.VIDEO,
  EXPORT_TYPE.PPTX,
  EXPORT_TYPE.JOIN_AUDIO,
  EXPORT_TYPE.SEPARATE_AUDIO,
  EXPORT_TYPE.SRT,
  EXPORT_TYPE.AUDIO_SRT,
];

export const NOTE_MAX_LENGTH = 5000;

export const BLOCK_STATUS = {
  ALL: 'ALL',
  DRAFT: 'DRAFT',
  AUDIO_AVAILABLE: 'AUDIO_AVAILABLE',
  IN_PROGRESS: 'IN_PROGRESS',
  ERROR: 'ERROR',
  AUDIO_EXPIRED: 'AUDIO_EXPIRED',
};

export const BLOCK_STATUS_LABEL = {
  [BLOCK_STATUS.ALL]: {
    label: 'allStatus',
    value: BLOCK_STATUS.ALL,
  },
  [BLOCK_STATUS.AUDIO_AVAILABLE]: {
    label: 'audioAvailable',
    value: BLOCK_STATUS.AUDIO_AVAILABLE,
  },
  [BLOCK_STATUS.IN_PROGRESS]: {
    label: 'inProgress',
    value: BLOCK_STATUS.IN_PROGRESS,
  },
  [BLOCK_STATUS.DRAFT]: {
    label: 'draft',
    value: BLOCK_STATUS.DRAFT,
  },
  [BLOCK_STATUS.AUDIO_EXPIRED]: {
    label: 'audioExpired',
    value: BLOCK_STATUS.AUDIO_EXPIRED,
  },
  [BLOCK_STATUS.ERROR]: {
    label: 'errorStatus',
    value: BLOCK_STATUS.ERROR,
  },
};

export const VALID_BREAK_TIME = {
  MIN: 0,
  MAX: 60,
};

export const VALID_BREAK_TIME_REGEX = /^[0-9.]+$/;
export const VALID_SPEED_REGEX = /^[0-9.]+$/;
export const NAME_REGEX = /^[\p{L}\p{N}\s\-_()]+$/u; // Letters, numbers, spaces, -, _, (, )
export const TITLE_FILE_REGEX = /[^\p{L}\p{N}\s\-_()]/gu; // Only allow letters, numbers, spaces, -, _, (, )
export const SRT_TIME_REGEX =
  /^(?:[0-9]{2}:[0-5][0-9]:[0-5][0-9][.,][0-9]{3})$/; // Eg: 00:59:59,000
export const DIGIT_SRT_TIME_REGEX = /^\d{2}:\d{2}:\d{2},\d{3}$/;
export const SYNTHESIS_CONTEXT = {
  MULTI_BLOCK: 'MULTI_BLOCK',
  SINGLE_BLOCK: 'SINGLE_BLOCK',
};

export const MAX_SPEAKERS = 10;

export const MAX_SPEAKER_FREE_LIMIT = 1;

export const EXPIRED_PACKAGE_MAX_PROJECTS = 3;

export const MAX_TOTAL_CHARACTERS = 100000;

export const DUBBING_MAX_DURATION = 4 * 3600; // 4 hours in seconds

export const TRANSLATE_CREDIT_PER_CHAR = 0.005;

export const REWRITE_CREDIT_PER_CHAR = 0.57;

export const TRANSLATION_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const REWRITE_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILURE: 'FAILURE',
};

export const TRANSLATE_WARNING_TYPE = {
  INITIAL_TRANSLATION_WITH_AUDIO: 'INITIAL_TRANSLATION_WITH_AUDIO',
  OTHER_LANGUAGE_TRANSLATION: 'OTHER_LANGUAGE_TRANSLATION',
};

export const PROJECT_TYPE = {
  AUDIO: 'AUDIO',
  VIDEO: 'VIDEO',
  PRESENTATION: 'PRESENTATION',
};

export const PROJECT_TYPE_KIND = {
  VIDEO: 'VIDEO',
  SRT: 'SRT',
  SLIDE: 'SLIDE',
  AUDIO: 'AUDIO',
};

/** Row icon and label per kind, shared by both project lists. */
export const PROJECT_TYPE_ICONS = {
  [PROJECT_TYPE_KIND.VIDEO]: {
    icon: VideoProjectIcon,
    label: 'projectTypeVideo',
  },
  [PROJECT_TYPE_KIND.SRT]: { icon: SrtProjectIcon, label: 'projectTypeSrt' },
  [PROJECT_TYPE_KIND.SLIDE]: {
    icon: PresentationProjectIcon,
    label: 'projectTypeSlide',
  },
  [PROJECT_TYPE_KIND.AUDIO]: {
    icon: NormalProjectIcon,
    label: 'projectTypeAudio',
  },
};

export const AUTO_SAVE_STATUS = {
  IDLE: 'IDLE',
  SAVING: 'SAVING',
  SAVED: 'SAVED',
  ERROR: 'ERROR',
  BLOCKED: 'BLOCKED',
  // The project no longer exists on the Backend (deleted elsewhere). Saving can
  // never succeed, so we show a banner and stop retrying.
  DELETED: 'DELETED',
};

// Auto-save request types — each maps to exactly one Backend endpoint.
export const AUTO_SAVE_REQUEST_TYPE = {
  SAVE_CAPTION_CONFIG: 'SAVE_CAPTION_CONFIG', // POST/PUT /caption-templates
  UPDATE_CONFIG: 'UPDATE_CONFIG', // PATCH /projects/:id        (title/audioType)
  UPDATE_BLOCK_FIELDS: 'UPDATE_BLOCK_FIELDS', // PATCH /projects/:id/blocks (block fields)
  DELETE_BLOCKS: 'DELETE_BLOCKS', // DELETE /projects/:id/blocks (removed blocks)
  REORDER_BLOCKS: 'REORDER_BLOCKS', // PATCH /projects/:id/block-order (full order)
  ADD_BLOCKS: 'ADD_BLOCKS', // POST /projects/:id/blocks (append new blocks)
};

export const CONTENT_FIELDS_BY_PRODUCT = {
  [PROJECT_PRODUCT.TTS]: ['title', 'text', 'voiceCode', 'speed', 'emphasis'],
  [PROJECT_PRODUCT.PRESENTATION]: [
    'title',
    'text',
    'voiceCode',
    'speed',
    'emphasis',
    'currentNote',
  ],
  // Dubbing block fields map 1:1 to the API (see buildBlockCreatePayload), so no
  // STORE_TO_API_FIELD entries are needed. videoSettings (project-level) is
  // handled separately and is not part of per-block content.
  [PROJECT_PRODUCT.DUBBING]: [
    'text',
    'translatedText',
    'speed',
    'startTime',
    'endTime',
    'speakerId',
  ],
};

export const STORE_TO_API_FIELD = { emphasis: 'emphasisIntensity' };

export const AUTO_SAVE_DEBOUNCE_MS = 1500;
export const AUTO_SAVE_RETRY_MS = 5000;
// Add-block requests target distinct anchors, so they can run concurrently. Cap
// the in-flight count so a scattered undo (many batches) saves fast without
// flooding the server.
export const AUTO_SAVE_MAX_CONCURRENT_ADD_REQUESTS = 5;
// How long the "Saved" status stays before reverting to IDLE
export const AUTO_SAVE_SAVED_DURATION_MS = 1000;
// Max time to wait for a flush to settle (SAVED) before an action proceeds
// anyway — e.g. synthesis flushes pending edits first, but must not hang
// forever if the save stalls.
export const AUTO_SAVE_FLUSH_TIMEOUT_MS = 15000;
